import { MortgageSettings, SIM_MAX_MONTHS } from '../money.model';

/** One month of the schedule. `balanceCents` is what remains AFTER the payment. */
export interface MonthRow {
  /** 0-based from today. */
  month: number;
  interestCents: number;
  principalCents: number;
  balanceCents: number;
}

const BASIS_POINTS_PER_UNIT = 10000;
const MONTHS_PER_YEAR = 12;

const monthlyRate = (rateBasisPoints: number): number =>
  rateBasisPoints / BASIS_POINTS_PER_UNIT / MONTHS_PER_YEAR;

/**
 * Scheduled principal & interest for the remaining term.
 *
 * There is deliberately no stored payment field. A real mortgage payment
 * includes taxes and insurance, and amortizing escrow as though it were
 * principal would make every figure on both screens wrong. Deriving the
 * payment keeps balance, rate and term self-consistent; a mismatch against
 * the statement IS the escrow, and saying so is more useful than accepting a
 * number that quietly breaks the math.
 *
 * Rounded UP, not to nearest: rounding down leaves a residual cent of
 * interest each month that the final payment never clears, so the loan would
 * run past its own term.
 */
export function monthlyPayment(m: MortgageSettings): number {
  if (m.principalCents <= 0 || m.termMonths <= 0) return 0;

  const r = monthlyRate(m.rateBasisPoints);
  // The annuity formula divides by the rate, so a zero rate takes the linear
  // form rather than producing NaN. Same guard as projectBalance.
  if (r === 0) return Math.ceil(m.principalCents / m.termMonths);

  const growth = Math.pow(1 + r, m.termMonths);
  return Math.ceil(m.principalCents * r * growth / (growth - 1));
}

/**
 * The month-by-month schedule until the balance clears.
 *
 * Every figure on the Mortgage screen is read off this one array, so the
 * chart and the table cannot disagree — the same discipline that has
 * `milestoneRows` read `projectBalance`'s series rather than recomputing it.
 *
 * Returns an empty schedule when the payment cannot cover one month of
 * interest. That loan never amortizes, and an empty array is how callers
 * learn it rather than by waiting on a loop that never terminates.
 */
export function amortize(m: MortgageSettings): MonthRow[] {
  const payment = monthlyPayment(m);
  if (m.principalCents <= 0 || payment <= 0) return [];

  const extra = Math.max(m.extraMonthlyCents, 0);
  const r = monthlyRate(m.rateBasisPoints);

  let balance = m.principalCents;
  const rows: MonthRow[] = [];

  for (let month = 0; month < SIM_MAX_MONTHS; month++) {
    const interestCents = Math.round(balance * r);
    const available = payment + extra - interestCents;
    // When the derived payment rounds to the same cent as the month's interest
    // — a tiny balance spread over a long term — nothing is left to pay toward
    // principal. The loan cannot amortize in whole cents, so an empty schedule
    // is the correct answer rather than spinning in an infinite loop.
    if (available <= 0) return [];

    const principalCents = Math.min(available, balance);
    balance -= principalCents;
    rows.push({ month, interestCents, principalCents, balanceCents: balance });
    if (balance <= 0) break;
  }

  return rows;
}

/**
 * Balance owed at the START of `month` (month 0 = today).
 *
 * An empty schedule against a positive principal is a stalled loan — the
 * payment never covers the interest — so the balance holds at the principal
 * forever rather than reporting a payoff that never happens.
 */
export function balanceAtMonth(
  schedule: MonthRow[], principalCents: number, month: number,
): number {
  const principal = Math.max(principalCents, 0);
  if (month <= 0 || !schedule.length) return principal;
  return schedule[Math.min(month, schedule.length) - 1].balanceCents;
}
